import React, { useEffect, useState, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import './Admin.css';

interface Campaign {
  id: string;
  name: string;
  status: string;
  objective: string;
  daily_budget: number | null;
  lifetime_budget: number | null;
  budget_remaining: number | null;
  spend: number;
  impressions: number;
  clicks: number;
  cpc: number;
  cpm: number;
  ctr: number;
  reach: number;
  purchases: number;
  add_to_carts: number;
  start_time: string;
  created_time: string;
}

interface InsightsDetail {
  campaign_name: string;
  spend: number;
  impressions: number;
  clicks: number;
  cpc: number;
  cpm: number;
  ctr: number;
  reach: number;
  frequency: number;
  purchases: number;
  add_to_carts: number;
  leads: number;
}

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_3d', label: 'Last 3 Days' },
  { value: 'last_7d', label: 'Last 7 Days' },
  { value: 'last_14d', label: 'Last 14 Days' },
  { value: 'last_30d', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

const MetaAdsReport: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('last_7d');
  const [configName, setConfigName] = useState('');
  const [noConfig, setNoConfig] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [insights, setInsights] = useState<InsightsDetail | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false, message: '', type: 'success'
  });

  const statusColors: Record<string, { bg: string; color: string }> = {
    ACTIVE: { bg: '#d1fae5', color: '#059669' },
    PAUSED: { bg: '#fef3c7', color: '#d97706' },
    DELETED: { bg: '#fee2e2', color: '#dc2626' },
    ARCHIVED: { bg: '#e5e7eb', color: '#6b7280' },
  };

  const columnDefs: ColDef[] = [
    { field: 'name', headerName: 'Campaign Name', flex: 2, sortable: true, filter: true,
      cellStyle: { fontWeight: '600', color: '#1f2937', cursor: 'pointer' },
      onCellClicked: (params) => handleCampaignClick(params.data)
    },
    {
      field: 'status', headerName: 'Status', width: 110, sortable: true,
      cellRenderer: (params: any) => {
        const c = statusColors[params.value] || statusColors.ARCHIVED;
        return <span style={{ background: c.bg, color: c.color, padding: '4px 12px', borderRadius: '12px', fontWeight: '600', fontSize: '12px' }}>{params.value}</span>;
      }
    },
    { field: 'objective', headerName: 'Objective', width: 160, sortable: true, cellStyle: { fontSize: '12px', color: '#6b7280' } },
    {
      field: 'daily_budget', headerName: 'Daily Budget', width: 120, sortable: true,
      valueFormatter: (params) => params.value != null ? `৳${params.value.toFixed(2)}` : '—'
    },
    {
      field: 'spend', headerName: 'Spend', width: 110, sortable: true,
      cellStyle: { fontWeight: '600', color: '#7c3aed' },
      valueFormatter: (params) => `৳${Number(params.value || 0).toFixed(2)}`
    },
    {
      field: 'impressions', headerName: 'Impressions', width: 120, sortable: true,
      valueFormatter: (params) => Number(params.value || 0).toLocaleString()
    },
    {
      field: 'clicks', headerName: 'Clicks', width: 90, sortable: true,
      valueFormatter: (params) => Number(params.value || 0).toLocaleString()
    },
    {
      field: 'ctr', headerName: 'CTR', width: 85, sortable: true,
      valueFormatter: (params) => `${Number(params.value || 0).toFixed(2)}%`,
      cellStyle: { fontWeight: '600' }
    },
    {
      field: 'cpc', headerName: 'CPC', width: 90, sortable: true,
      valueFormatter: (params) => `৳${Number(params.value || 0).toFixed(2)}`
    },
    {
      field: 'reach', headerName: 'Reach', width: 100, sortable: true,
      valueFormatter: (params) => Number(params.value || 0).toLocaleString()
    },
    {
      field: 'purchases', headerName: 'Purchases', width: 100, sortable: true,
      cellStyle: { fontWeight: '600', color: '#059669' }
    },
  ];

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      setNoConfig(false);
      const response = await adminAPI.getMetaAdsCampaigns(datePreset);
      const data = response.data || response;
      setCampaigns(data.campaigns || []);
      setConfigName(data.config_name || '');
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNoConfig(true);
        setCampaigns([]);
      } else {
        const msg = error.response?.data?.error || 'Failed to load campaigns';
        setErrorMsg(msg);
        setCampaigns([]);
      }
    } finally {
      setLoading(false);
    }
  }, [datePreset]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleCampaignClick = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setLoadingInsights(true);
    try {
      const response = await adminAPI.getMetaAdsCampaignInsights(campaign.id, datePreset);
      setInsights(response.data || response);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to load insights';
      setToast({ show: true, message: msg, type: 'error' });
      setInsights(null);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Summary metrics
  const totalSpend = campaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
  const totalImpressions = campaigns.reduce((sum, c) => sum + (c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0);
  const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
  const totalPurchases = campaigns.reduce((sum, c) => sum + (c.purchases || 0), 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'ACTIVE').length;

  const cardStyle: React.CSSProperties = {
    background: 'white', borderRadius: '10px', padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, minWidth: '140px',
    border: '1px solid #f3f4f6'
  };
  const cardLabelStyle: React.CSSProperties = { fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const cardValueStyle: React.CSSProperties = { fontSize: '24px', fontWeight: '700', marginTop: '4px' };

  // No config state
  if (noConfig) {
    return (
      <div className="admin-page">
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📡</div>
          <h2 style={{ color: '#1f2937', marginBottom: '8px' }}>Meta Ads Not Connected</h2>
          <p style={{ color: '#6b7280', maxWidth: '400px', margin: '0 auto 24px' }}>
            Configure your Meta Marketing API credentials to view campaign performance data.
          </p>
          <a href="/admin/meta-ads/config" style={{
            display: 'inline-block', padding: '10px 24px', background: '#2563eb', color: 'white',
            borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: '14px'
          }}>
            Configure Meta Ads →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Meta Ads Report</h3>
          <p>Campaign performance data from {configName || 'your Meta account'}</p>
        </div>
        <div className="management-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: 'white', cursor: 'pointer' }}>
            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={loadCampaigns}>🔄 Refresh</button>
        </div>
      </div>

      {errorMsg && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#991b1b', fontSize: '14px' }}>
          ⚠️ {errorMsg}
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Total Spend</div>
          <div style={{ ...cardValueStyle, color: '#7c3aed' }}>৳{totalSpend.toFixed(2)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Impressions</div>
          <div style={{ ...cardValueStyle, color: '#0369a1' }}>{totalImpressions.toLocaleString()}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Clicks</div>
          <div style={{ ...cardValueStyle, color: '#0d9488' }}>{totalClicks.toLocaleString()}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Avg CTR</div>
          <div style={{ ...cardValueStyle, color: '#ea580c' }}>{avgCTR.toFixed(2)}%</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Purchases</div>
          <div style={{ ...cardValueStyle, color: '#059669' }}>{totalPurchases}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Active</div>
          <div style={{ ...cardValueStyle, color: '#2563eb' }}>{activeCampaigns}</div>
        </div>
      </div>

      {/* Campaigns Grid */}
      <div className="ag-grid-wrapper">
        <div className="results-info">
          <span>{campaigns.length}</span> campaign(s) found
          {campaigns.length > 0 && <span style={{ marginLeft: '8px', color: '#9ca3af', fontSize: '12px' }}>Click a campaign name for detailed insights</span>}
        </div>
        <div className="ag-grid-container">
          <AgGridReact
            rowData={campaigns}
            columnDefs={columnDefs}
            theme={themeQuartz}
            defaultColDef={{ resizable: true }}
            animateRows={true}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
          />
        </div>
      </div>

      {/* Campaign Insights Detail Panel */}
      {selectedCampaign && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '600px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>{selectedCampaign.name}</h3>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>{selectedCampaign.objective} • {selectedCampaign.status}</span>
              </div>
              <button onClick={() => { setSelectedCampaign(null); setInsights(null); }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            {loadingInsights ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                Loading insights...
              </div>
            ) : insights ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                {[
                  { label: 'Spend', value: `৳${insights.spend.toFixed(2)}`, color: '#7c3aed' },
                  { label: 'Impressions', value: insights.impressions.toLocaleString(), color: '#0369a1' },
                  { label: 'Clicks', value: insights.clicks.toLocaleString(), color: '#0d9488' },
                  { label: 'CTR', value: `${insights.ctr.toFixed(2)}%`, color: '#ea580c' },
                  { label: 'CPC', value: `৳${insights.cpc.toFixed(2)}`, color: '#d97706' },
                  { label: 'CPM', value: `৳${insights.cpm.toFixed(2)}`, color: '#be185d' },
                  { label: 'Reach', value: insights.reach.toLocaleString(), color: '#4f46e5' },
                  { label: 'Frequency', value: insights.frequency.toFixed(2), color: '#0891b2' },
                  { label: 'Purchases', value: String(insights.purchases), color: '#059669' },
                  { label: 'Add to Carts', value: String(insights.add_to_carts), color: '#7c3aed' },
                  { label: 'Leads', value: String(insights.leads), color: '#dc2626' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>{item.label}</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: item.color, marginTop: '4px' }}>{item.value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                No insight data available for this period.
              </div>
            )}
          </div>
        </div>
      )}

      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default MetaAdsReport;
